import { Form } from '../services/formService';
import { Job } from '../services/jobService';
import { PDFFont } from 'pdf-lib';
import { TextSegment } from './richTextParser';

// Auto-sizing font constants
export const DEFAULT_MIN_FONT_SIZE = 4;
export const DEFAULT_LINE_HEIGHT_RATIO = 1.2;
export const FIELD_PADDING = 4;
export const EDITOR_FONT_FAMILY = 'Helvetica, Arial, sans-serif';

/**
 * Scales an image to fit ("contain") within a box while preserving aspect ratio.
 * The result never exceeds the box in either dimension; it scales up or down so
 * the image is as large as the box allows. Returns { width: 0, height: 0 } for
 * any non-positive input.
 */
export function computeContainFit(
  boxWidth: number,
  boxHeight: number,
  imageWidth: number,
  imageHeight: number
): { width: number; height: number } {
  if (boxWidth <= 0 || boxHeight <= 0 || imageWidth <= 0 || imageHeight <= 0) {
    return { width: 0, height: 0 };
  }
  const scale = Math.min(boxWidth / imageWidth, boxHeight / imageHeight);
  return { width: imageWidth * scale, height: imageHeight * scale };
}

// Text measurer interface for abstraction between pdf-lib and Canvas
export interface TextMeasurer {
  measureTextWidth(text: string, fontSize: number): number;
}

/**
 * Creates a text measurer using a pdf-lib PDFFont.
 * Used for PDF generation to ensure accurate text measurement.
 */
export function createPdfFontMeasurer(font: PDFFont): TextMeasurer {
  return {
    measureTextWidth(text: string, fontSize: number): number {
      try {
        return font.widthOfTextAtSize(text, fontSize);
      } catch {
        // Fallback: estimate width based on character count
        return text.length * fontSize * 0.5;
      }
    }
  };
}

/**
 * Creates a text measurer using browser Canvas.
 * Used for interactive editor to match visual rendering.
 */
export function createCanvasFontMeasurer(fontFamily: string = 'Helvetica, Arial, sans-serif'): TextMeasurer {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  return {
    measureTextWidth(text: string, fontSize: number): number {
      if (!ctx) {
        // Fallback if canvas context unavailable
        return text.length * fontSize * 0.5;
      }
      ctx.font = `${fontSize}px ${fontFamily}`;
      return ctx.measureText(text).width;
    }
  };
}

/**
 * Wraps text into lines that fit within the specified width.
 * Works with any TextMeasurer implementation for consistency.
 */
export function wrapTextWithMeasurer(
  text: string,
  maxWidth: number,
  fontSize: number,
  measurer: TextMeasurer
): string[] {
  if (!text || maxWidth <= 0) return [];

  const lines: string[] = [];
  // Split by explicit newlines first
  const paragraphs = text.split('\n');

  for (const paragraph of paragraphs) {
    if (!paragraph.trim()) {
      lines.push('');
      continue;
    }

    const words = paragraph.split(/\s+/);
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const testWidth = measurer.measureTextWidth(testLine, fontSize);

      if (testWidth > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }
  }

  return lines;
}

export interface AutoSizeOptions {
  text: string;
  maxWidth: number;
  maxHeight: number;
  maxFontSize: number;
  minFontSize?: number;
  lineHeightRatio?: number;
  measurer: TextMeasurer;
}

export interface AutoSizeResult {
  fontSize: number;
  lineCount: number;
  fitsWithinBounds: boolean;
}

/**
 * Calculates the optimal font size to fit text within given dimensions.
 * Starts at maxFontSize and reduces by 0.5pt until text fits or minFontSize is reached.
 */
export function calculateAutoSizeFontSize(options: AutoSizeOptions): AutoSizeResult {
  const {
    text,
    maxWidth,
    maxHeight,
    maxFontSize,
    minFontSize = DEFAULT_MIN_FONT_SIZE,
    lineHeightRatio = DEFAULT_LINE_HEIGHT_RATIO,
    measurer
  } = options;

  if (!text || !text.trim()) {
    return { fontSize: maxFontSize, lineCount: 0, fitsWithinBounds: true };
  }

  // Account for field padding
  const availableWidth = maxWidth - FIELD_PADDING;
  const availableHeight = maxHeight - FIELD_PADDING;

  let currentFontSize = maxFontSize;
  let lines: string[] = [];
  let fitsWithinBounds = false;

  while (currentFontSize >= minFontSize) {
    lines = wrapTextWithMeasurer(text, availableWidth, currentFontSize, measurer);
    const lineHeight = currentFontSize * lineHeightRatio;
    const totalHeight = lines.length * lineHeight;

    if (totalHeight <= availableHeight) {
      fitsWithinBounds = true;
      break;
    }

    // Reduce font size by 0.5pt
    currentFontSize -= 0.5;
  }

  // Ensure we don't go below minimum
  if (currentFontSize < minFontSize) {
    currentFontSize = minFontSize;
    lines = wrapTextWithMeasurer(text, availableWidth, currentFontSize, measurer);
    fitsWithinBounds = false;
  }

  return {
    fontSize: currentFontSize,
    lineCount: lines.length,
    fitsWithinBounds
  };
}

export function calculateSingleLineAutoSizeFontSize(
  text: string,
  maxWidth: number,
  maxFontSize: number,
  minFontSize: number = DEFAULT_MIN_FONT_SIZE,
  measurer: TextMeasurer
): number {
  if (!text || !text.trim()) return maxFontSize;

  const availableWidth = maxWidth - FIELD_PADDING;
  let fontSize = maxFontSize;

  while (fontSize > minFontSize) {
    const textWidth = measurer.measureTextWidth(text, fontSize);
    if (textWidth <= availableWidth) {
      return fontSize;
    }
    fontSize -= 0.5;
  }

  return minFontSize;
}

/**
 * Converts a Uint8Array of bytes to a base64 string.
 * Processes in chunks to avoid call stack overflow with large files.
 * @param bytes The byte array to convert
 * @returns Promise resolving to base64 encoded string
 */
export const convertBytesToBase64 = async (bytes: Uint8Array): Promise<string> => {
  const chunkSize = 8192; // 8KB chunks
  let result = '';

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.slice(i, i + chunkSize);
    result += String.fromCharCode.apply(null, Array.from(chunk));
  }

  return btoa(result);
};

/**
 * Sanitizes a string for use in filenames by replacing non-alphanumeric characters with hyphens.
 * @param str The string to sanitize
 * @returns Sanitized string safe for filenames
 */
export const sanitizeForFilename = (str: string): string => {
  return str
    .replace(/[^a-zA-Z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Generates a PDF filename based on job number and form description.
 * Multi-instance forms get a suffix mirroring the backend convention:
 * index 1 uses the label alone (if any); copies beyond #1 always include the
 * index so duplicate labels can't collide (e.g. -2-Kitchen, or just -2).
 * @param job The job object (or object with jobNumber property)
 * @param form The form object (or object with description property)
 * @returns Generated filename in format: {jobNumber}-{formDescription}[-instanceSuffix].pdf
 */
export const generatePdfFilename = (
  job: Pick<Job, 'jobNumber'> | null | undefined,
  form: Pick<Form, 'description'>,
  instanceIndex: number = 1,
  instanceLabel?: string | null
): string => {
  const sanitizedFormName = sanitizeForFilename(form.description);
  const sanitizedJobNumber = job?.jobNumber
    ? sanitizeForFilename(job.jobNumber)
    : 'job';

  const labelSlug = instanceLabel ? sanitizeForFilename(instanceLabel) : '';
  let suffix = '';
  if (instanceIndex > 1) {
    suffix = labelSlug ? `-${instanceIndex}-${labelSlug}` : `-${instanceIndex}`;
  } else if (labelSlug) {
    suffix = `-${labelSlug}`;
  }

  return `${sanitizedJobNumber}-${sanitizedFormName}${suffix}.pdf`;
};

/**
 * Creates a blob URL from PDF bytes and triggers a download.
 * @param pdfBytes The PDF content as Uint8Array
 * @param filename The filename for the download
 */
export const downloadPdfBytes = (pdfBytes: Uint8Array, filename: string): void => {
  const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up URL after download
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10000);
};

/**
 * Opens a PDF in a hidden iframe and triggers the print dialog.
 * @param pdfBytes The PDF content as Uint8Array
 * @returns Promise that resolves when print dialog is opened
 */
export const printPdfBytes = (pdfBytes: Uint8Array): Promise<void> => {
  return new Promise((resolve) => {
    const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      // Give it some time to open the print dialog before cleaning up
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
        resolve();
      }, 60000);
    };
  });
};

/**
 * Opens a PDF in a new browser tab.
 * @param pdfBytes The PDF content as Uint8Array
 */
export const openPdfInNewTab = (pdfBytes: Uint8Array): void => {
  const blob = new Blob([pdfBytes as unknown as BlobPart], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);

  window.open(url, '_blank');

  // Clean up URL after a delay
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 10000);
};

// ─── Rich Text Measurer & Formatted Wrapping ────────────────────────────────

/**
 * Measurer that accounts for bold and size offsets in rich text segments.
 */
export interface RichTextMeasurer {
  measureWidth(text: string, fontSize: number, bold: boolean): number;
}

/**
 * Creates a RichTextMeasurer using pdf-lib fonts.
 */
export function createPdfRichTextMeasurer(regularFont: PDFFont, boldFont: PDFFont): RichTextMeasurer {
  return {
    measureWidth(text: string, fontSize: number, bold: boolean): number {
      const font = bold ? boldFont : regularFont;
      try {
        return font.widthOfTextAtSize(text, fontSize);
      } catch {
        return text.length * fontSize * 0.5;
      }
    }
  };
}

/**
 * Creates a RichTextMeasurer using browser Canvas.
 */
export function createCanvasRichTextMeasurer(fontFamily: string = 'Helvetica, Arial, sans-serif'): RichTextMeasurer {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  return {
    measureWidth(text: string, fontSize: number, bold: boolean): number {
      if (!ctx) {
        return text.length * fontSize * 0.5;
      }
      ctx.font = `${bold ? 'bold ' : ''}${fontSize}px ${fontFamily}`;
      return ctx.measureText(text).width;
    }
  };
}

/**
 * A segment within a wrapped line, carrying its style.
 */
export interface FormattedLineSegment {
  text: string;
  style: { bold: boolean; sizeOffset: number };
}

/**
 * A single wrapped line of formatted text.
 */
export interface FormattedLine {
  segments: FormattedLineSegment[];
  lineHeight: number;
}

/**
 * An atomic "word group" that belongs together on one line.
 * It is a sequence of styled fragments between whitespace boundaries.
 */
interface WordGroup {
  fragments: FormattedLineSegment[];
  totalWidth: number;
}

/**
 * Wraps formatted text segments into lines that fit within maxWidth.
 * Word groups are atomic units split on whitespace boundaries.
 */
export function wrapFormattedText(
  segments: TextSegment[],
  maxWidth: number,
  baseFontSize: number,
  lineHeightRatio: number,
  measurer: RichTextMeasurer
): FormattedLine[] {
  if (!segments.length || maxWidth <= 0) return [];

  // Flatten segments into characters with styles, then split into paragraphs and word groups
  // Strategy: split each segment by \n first, then by whitespace within each paragraph.

  // First, split segments by newlines into paragraph-level segment arrays
  const paragraphs: TextSegment[][] = [[]];
  for (const seg of segments) {
    const parts = seg.text.split('\n');
    for (let i = 0; i < parts.length; i++) {
      if (i > 0) {
        paragraphs.push([]);
      }
      if (parts[i].length > 0) {
        paragraphs[paragraphs.length - 1].push({ text: parts[i], style: { ...seg.style } });
      }
    }
  }

  const lines: FormattedLine[] = [];

  for (const paraSegments of paragraphs) {
    if (paraSegments.length === 0) {
      // Empty line (explicit \n)
      lines.push({ segments: [], lineHeight: baseFontSize * lineHeightRatio });
      continue;
    }

    // Build word groups from paragraph segments
    const wordGroups = buildWordGroups(paraSegments, baseFontSize, measurer);

    // Greedy line wrapping
    let currentLineGroups: WordGroup[] = [];
    let currentLineWidth = 0;

    const resolveSize = (offset: number) => Math.max(1, baseFontSize + offset);

    const spaceWidth = measurer.measureWidth(' ', baseFontSize, false);

    const flushLine = () => {
      if (currentLineGroups.length === 0) return;
      const lineSegments: FormattedLineSegment[] = [];
      let maxSize = baseFontSize;
      for (let g = 0; g < currentLineGroups.length; g++) {
        if (g > 0) {
          // Add space between word groups
          lineSegments.push({ text: ' ', style: { bold: false, sizeOffset: 0 } });
        }
        for (const frag of currentLineGroups[g].fragments) {
          lineSegments.push(frag);
          const fragSize = resolveSize(frag.style.sizeOffset);
          if (fragSize > maxSize) maxSize = fragSize;
        }
      }
      lines.push({ segments: lineSegments, lineHeight: maxSize * lineHeightRatio });
      currentLineGroups = [];
      currentLineWidth = 0;
    };

    for (const wg of wordGroups) {
      const widthWithSpace = currentLineGroups.length > 0 ? spaceWidth + wg.totalWidth : wg.totalWidth;
      if (currentLineGroups.length > 0 && currentLineWidth + widthWithSpace > maxWidth) {
        flushLine();
      }
      currentLineGroups.push(wg);
      currentLineWidth += (currentLineGroups.length > 1 ? spaceWidth : 0) + wg.totalWidth;
    }
    flushLine();
  }

  return lines;
}

/**
 * Builds word groups from paragraph segments. A word group is text between whitespace.
 * Intra-word tags (e.g. "hel<b>lo</b>") produce a single word group with multiple fragments.
 */
function buildWordGroups(
  segments: TextSegment[],
  baseFontSize: number,
  measurer: RichTextMeasurer
): WordGroup[] {
  const groups: WordGroup[] = [];
  let currentFragments: FormattedLineSegment[] = [];
  let currentWidth = 0;

  const resolveSize = (offset: number) => Math.max(1, baseFontSize + offset);

  const flush = () => {
    if (currentFragments.length > 0) {
      groups.push({ fragments: currentFragments, totalWidth: currentWidth });
      currentFragments = [];
      currentWidth = 0;
    }
  };

  for (const seg of segments) {
    // Split segment text by whitespace, preserving boundaries
    const parts = seg.text.split(/(\s+)/);
    for (const part of parts) {
      if (!part) continue;
      if (/^\s+$/.test(part)) {
        // Whitespace: flush current word group
        flush();
      } else {
        // Non-whitespace: add to current word group
        const fontSize = resolveSize(seg.style.sizeOffset);
        const w = measurer.measureWidth(part, fontSize, seg.style.bold);
        currentFragments.push({ text: part, style: { bold: seg.style.bold, sizeOffset: seg.style.sizeOffset } });
        currentWidth += w;
      }
    }
  }
  flush();

  return groups;
}

export interface FormattedAutoSizeOptions {
  segments: TextSegment[];
  maxWidth: number;
  maxHeight: number;
  maxFontSize: number;
  minFontSize?: number;
  lineHeightRatio?: number;
  measurer: RichTextMeasurer;
}

export interface FormattedAutoSizeResult {
  baseFontSize: number;
  lines: FormattedLine[];
  fitsWithinBounds: boolean;
}

/**
 * Calculates the optimal base font size for formatted text.
 * Same decremental loop as calculateAutoSizeFontSize but operates on TextSegments.
 * Only the base font size shrinks; absolute size offsets stay fixed.
 */
export function calculateFormattedAutoSizeFontSize(options: FormattedAutoSizeOptions): FormattedAutoSizeResult {
  const {
    segments,
    maxWidth,
    maxHeight,
    maxFontSize,
    minFontSize = DEFAULT_MIN_FONT_SIZE,
    lineHeightRatio = DEFAULT_LINE_HEIGHT_RATIO,
    measurer
  } = options;

  if (!segments.length) {
    return { baseFontSize: maxFontSize, lines: [], fitsWithinBounds: true };
  }

  const availableWidth = maxWidth - FIELD_PADDING;
  const availableHeight = maxHeight - FIELD_PADDING;

  let currentFontSize = maxFontSize;
  let wrappedLines: FormattedLine[] = [];
  let fitsWithinBounds = false;

  while (currentFontSize >= minFontSize) {
    wrappedLines = wrapFormattedText(segments, availableWidth, currentFontSize, lineHeightRatio, measurer);
    const totalHeight = wrappedLines.reduce((sum, line) => sum + line.lineHeight, 0);

    if (totalHeight <= availableHeight) {
      fitsWithinBounds = true;
      break;
    }

    currentFontSize -= 0.5;
  }

  if (currentFontSize < minFontSize) {
    currentFontSize = minFontSize;
    wrappedLines = wrapFormattedText(segments, availableWidth, currentFontSize, lineHeightRatio, measurer);
    fitsWithinBounds = false;
  }

  return {
    baseFontSize: currentFontSize,
    lines: wrappedLines,
    fitsWithinBounds
  };
}
